<script lang="ts">
  import { onDestroy } from "svelte";
  import { field } from "svelte-forms2";
  import { required } from "svelte-forms2/validators";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { extractGooglePlaceData } from "@onboarding2/services/google_places_autocomplete_service";

  // Components
  import FlashWrapper from "@shared/svelte/Components/Flash/Wrapper.svelte";
  import PageTitle from "@shared/svelte/Components/PageTitle.svelte";
  import RetailerAutocompleteSignup from "@onboarding2/svelte/RetailerDetails/RetailerAutocompleteSignup.svelte";

  // Stores
  import RetailerStore from "@onboarding2/svelte/Stores/RetailerStore";

  // Types
  import type { Errors } from "@shared/types/Errors";
  import type { RetailerType, CreateRetailer } from "@shared/types/Retailer";

  export let enabledStates: { data: { state: string; onboarding_delivery_dates: boolean }[] };
  export let retailers: RetailerType[] = [];

  const { trackClick } = EventTracking;

  const initRetailer: RetailerType | CreateRetailer = findOrInitializeRetailer(retailers);

  let errors: Errors = { messages: [] };
  let googlePlaceResult = null;
  let retailerStore = RetailerStore(
    initRetailer,
    "/api/retailer/include_distributors=1",
    "/api/onboarding/update",
  );
  let retailer: RetailerType = $retailerStore;
  let source: string = null;

  const liquorLicense = field("liquor_license", retailer.liquor_license, []);
  const name = field("name", retailer.name, [required()]);
  const nonAlcoholic = field("non_alcoholic", retailer.non_alcoholic || false, [required()]);
  const licenseType = field("license_type", retailer.license_type, [required()], {
    checkOnInit: false,
  });

  const unsubscribeErrors = retailerStore.errors.subscribe((n: Errors) => (errors = n));

  const selectBusinessFromAutocomplete = (place: any): void => {
    googlePlaceResult = place;

    trackClick("onboarding_google_place_autocomplete");

    const formattedResults = extractGooglePlaceData(place);

    // There does not seem to be any way to filter autocomplete places by state
    if (
      enabledStates.data.map(enabledState => enabledState.state).includes(formattedResults.state)
    ) {
      name.set(formattedResults.name);

      errors = { messages: [] };
    } else {
      errors = {
        messages: [
          "Provi is not available in your market. Contact us at support@provi.com to find out when we'll be available.",
        ],
      };
    }
  };

  const unselect = (_e: CustomEvent<null>): void => {
    googlePlaceResult = null;
    name.reset();
  };

  function findOrInitializeRetailer(retailers: RetailerType[]): RetailerType | CreateRetailer {
    let retailer: RetailerType | CreateRetailer;

    if (!!retailers.length) retailer = retailers[0];

    if (!retailer && window["$rootScope"]?.getCurrentRetailer) {
      retailer = window["$rootScope"]?.getCurrentRetailer();
    }

    if (!retailer) {
      retailer = {
        license_type: null,
        liquor_license_ack: undefined,
        non_alcoholic: false,
        state: null,
      };
    }

    return retailer;
  }

  $: hasSelectedRetailer = !!googlePlaceResult;

  window["initGoogleMaps"] = () => {
    const google: any = window["google"];

    if (!google) return;

    const autocompleteInput = document.getElementById("name");
    const autocomplete = new google.maps.places.Autocomplete(autocompleteInput, {
      types: ["establishment"],
      componentRestrictions: { country: "US" },
      language: "en",
    });

    autocomplete.setFields([
      "place_id",
      "name",
      "address_components",
      "formatted_address",
      "types",
      "business_status",
    ]);
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();

      if (place && place.address_components) selectBusinessFromAutocomplete(place);
    });
  };

  if (!["test"].includes(window["C"].env)) {
    const script = document.createElement("script");

    script.src = [
      "https://maps.googleapis.com/maps/api/js?key=",
      window["C"].google_maps,
      "&callback=initGoogleMaps&libraries=places&components=country:us&sessiontoken=",
      window["$rootScope"]?.user?.id,
    ]
      .filter(path => !!path)
      .join("");

    document.body.appendChild(script);
  }

  if ((source = sessionStorage.getItem("sign_up_source"))) retailer.sign_up_source = source;

  onDestroy(unsubscribeErrors);
</script>

<PageTitle title="Set up your business - Sign up | Provi" />
<RetailerAutocompleteSignup
  {googlePlaceResult}
  {hasSelectedRetailer}
  {retailerStore}
  fields={{ name, licenseType, liquorLicense, nonAlcoholic }}
  bind:retailer
  on:unselected={unselect}
/>
<FlashWrapper />
